import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { HORIZONTAL_CHAPTERS } from '../../data/content';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';

gsap.registerPlugin(ScrollTrigger);

export const PinnedHorizontal: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;

    // Only apply pin on viewports wider than 1024px for optimal UX
    const matchMedia = window.matchMedia('(min-width: 1024px)');

    if (!matchMedia.matches) {
      return; // Fallback to normal flow on small screens
    }

    const totalWidth = track.scrollWidth - window.innerWidth + 120;

    const ctx = gsap.context(() => {
      gsap.to(track, {
        x: -totalWidth,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          pin: true,
          scrub: 1.2,
          start: 'top top',
          end: () => `+=${totalWidth * 1.5}`,
          invalidateOnRefresh: true,
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="specimens"
      ref={sectionRef}
      className="relative min-h-screen py-16 sm:py-24 lg:py-0 overflow-hidden bg-espresso-950 z-10 flex flex-col justify-center"
      aria-label="Specimens Horizontal Gallery"
    >
      {/* Header Info */}
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 mb-6 sm:mb-8 lg:mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 text-xs font-mono text-sunset-gold tracking-ultra uppercase mb-2.5 sm:mb-3">
            <PixelGlyph name="pulse-dot" size={10} />
            <span>EXHIBITS // ARCHITECTURAL BREAKTHROUGHS</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl md:text-5xl text-cream-100">
            Four pillars of craft.
          </h2>
        </div>
        <div className="hidden lg:flex text-xs font-mono text-cream-dim items-center gap-2">
          <span>SCROLL DOWN TO TRAVERSE HORIZONTALLY</span>
          <PixelGlyph name="arrow-right" size={14} color="#d4af37" />
        </div>
      </div>

      {/* Horizontal Track Container */}
      <div className="w-full overflow-x-auto lg:overflow-visible no-scrollbar">
        <div
          ref={trackRef}
          className="flex flex-col lg:flex-row items-stretch gap-6 sm:gap-8 px-4 sm:px-6 lg:px-24 w-full lg:w-max will-change-transform"
        >
          {HORIZONTAL_CHAPTERS.map((chapter) => (
            <div
              key={chapter.id}
              className="w-full lg:w-[680px] shrink-0"
              data-cursor="inspect"
            >
              <DoubleBezelCard
                tilt={true}
                innerClassName="p-6 sm:p-8 md:p-10 flex flex-col justify-between h-full min-h-[380px] sm:min-h-[440px] lg:min-h-[480px]"
                glowColor={`${chapter.accentColor}25`}
              >
                {/* Top Badge & Index */}
                <div>
                  <div className="flex items-center justify-between pb-4 sm:pb-6 mb-4 sm:mb-6 border-b border-white/10 text-xs font-mono">
                    <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-cream-300 tracking-wider text-[11px] sm:text-xs">
                      {chapter.tag}
                    </span>
                    <span
                      className="font-bold text-base sm:text-lg font-mono"
                      style={{ color: chapter.accentColor }}
                    >
                      [{chapter.index}/04]
                    </span>
                  </div>

                  <h3 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-cream-100 mb-2 sm:mb-3">
                    {chapter.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-cream-300 font-medium mb-3 sm:mb-4 leading-relaxed">
                    {chapter.subtitle}
                  </p>
                  <p className="text-xs sm:text-sm text-cream-muted leading-relaxed mb-6 sm:mb-8">
                    {chapter.description}
                  </p>
                </div>

                {/* Bottom Technical Metrics Grid (Swiss architectural style) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-2 pt-4 sm:pt-6 border-t border-white/10 swiss-grid-divide bg-espresso-950/50 rounded-xl p-3 border border-white/5">
                  {chapter.metrics.map((m, mIdx) => (
                    <div key={mIdx} className="px-1 sm:px-2 flex items-center justify-between sm:block">
                      <div className="text-[9px] sm:text-[10px] font-mono text-cream-dim tracking-wider uppercase mb-0 sm:mb-1">
                        {m.label}
                      </div>
                      <div
                        className="text-xs sm:text-sm md:text-base font-mono font-bold truncate"
                        style={{ color: chapter.accentColor }}
                      >
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>
              </DoubleBezelCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
